import React from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchUserReservations } from "../../api/auth";
import { fetchWorkLogs } from "../../api/workLogs";
import { cancelReservation, checkInEvent } from "../../api/reservations";
import { useAuth } from "../../hooks/useAuth";
import Panel from "./Panel";
import StatCard from "./StatCard";
import WorkLogPanel from "./WorkLogPanel";
import { buildActivityRows, computeParticipation } from "./volunteerActivity";

// The Upcoming panel's attendance column, once a check-in exists: what the row
// says and what it means for hours. Keyed by attendance_status, with the null
// (nobody has ruled yet) case falling back to pending.
const ATTENDANCE_COLUMN = {
    approved: {
        label: "Approved",
        title: "Attendance approved — counted toward your hours.",
    },
    no_show: {
        label: "No show",
        title: "An admin recorded this as a no-show, so it earns no hours. "
            + "Contact us if that is wrong.",
    },
    pending: {
        label: "Pending",
        title: "Checked in — awaiting admin approval. Hours count once approved.",
    },
};

const formatDate = (value) => {
    if (!value) return "TBD";
    const d = new Date(value);
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${mm}/${dd}/${d.getFullYear()}`;
};

export default function VolunteerDashboard({ locked }) {
    const { isAuthenticated } = useAuth();
    const queryClient = useQueryClient();

    const { data: reservations = [] } = useQuery({
        queryKey: ["reservations"],
        queryFn: fetchUserReservations,
        enabled: isAuthenticated,
    });

    // Same key as WorkLogPanel's, so react-query serves both from one cache
    // entry and the panel's mutations refresh these stats too.
    const { data: workLogs = [] } = useQuery({
        queryKey: ["workLogs", "volunteer"],
        queryFn: () => fetchWorkLogs("volunteer"),
        enabled: isAuthenticated,
    });

    const now = new Date();

    // UPCOMING: confirmed reservations whose event has not ended yet
    const upcomingEvents = reservations
        .filter((res) => {
            const endDate = res.event?.end_date ? new Date(res.event.end_date) : null;
            return endDate && endDate >= now && res.status === "confirmed";
        })
        .sort((a, b) => {
            const dateA = a.event?.start_date ? new Date(a.event.start_date) : new Date(9999, 0, 1);
            const dateB = b.event?.start_date ? new Date(b.event.start_date) : new Date(9999, 0, 1);
            return dateA - dateB;
        })
        .map((res) => {
            const startDate = res.event?.start_date ? new Date(res.event.start_date) : null;
            const endDate = res.event?.end_date ? new Date(res.event.end_date) : null;
            const checkInOpens = startDate ? new Date(startDate.getTime() - 60 * 60 * 1000) : null;
            const canCheckIn = checkInOpens && endDate && now >= checkInOpens && now <= endDate;
            return {
                id: res.id,
                eventId: res.event?.pk,
                name: res.event?.title || "Event",
                date: formatDate(res.event?.start_date),
                attendedAt: res.attended_at,
                attendance:
                    ATTENDANCE_COLUMN[res.attendance_status] || ATTENDANCE_COLUMN.pending,
                canCheckIn: canCheckIn && !res.attended_at,
            };
        });

    // Stats and History both count event attendance and approved self-directed
    // work; the rules live in volunteerActivity.js.
    const { time: participationTime, number: participationNumber } = computeParticipation(
        reservations,
        workLogs
    );
    const activityRows = buildActivityRows(reservations, workLogs, now);

    const cancelMutation = useMutation({
        mutationFn: cancelReservation,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["reservations"] }),
        onError: (error) =>
            alert("Failed to cancel reservation: " + (error.response?.data?.error || "Unknown error")),
    });

    const checkInMutation = useMutation({
        mutationFn: checkInEvent,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ["reservations"] }),
        onError: (error) =>
            alert("Check-in failed: " + (error.response?.data?.error || "Unknown error")),
    });

    const handleCancel = (reservationId) => {
        if (window.confirm("Are you sure you want to cancel this reservation?")) {
            cancelMutation.mutate(reservationId);
        }
    };

    return (
        <>
            {locked && (
                <div
                    className="absolute inset-0 z-10 flex items-center justify-center px-6 text-center backdrop-blur-[8.2px]"
                    style={{ backgroundColor: "rgba(197,184,175,0.85)" }}
                >
                    <p className="text-[24px] text-[#1c2938] leading-normal">
                        Volunteer Dashboard is available to approved volunteers only.
                        <br />
                        Submit a{" "}
                        <Link to="/volunteer" className="underline">
                            Volunteer Application
                        </Link>{" "}
                        to unlock access.
                    </p>
                </div>
            )}
            {/* `inert` while locked: the overlay stops clicks but not Tab, and
                the Work form below it would otherwise be reachable and
                submittable by someone without the Volunteer role. */}
            <div
                inert={locked || undefined}
                className="grid grid-cols-1 xl:grid-cols-2 gap-x-[84px] gap-y-[38px] max-w-[1528px] h-full"
            >
                {/* Left column */}
                <div className="flex flex-col gap-[38px] min-h-0">
                    {/* Stat cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-[56px] shrink-0">
                        <StatCard
                            label={"PARTICIPATION\nTIME"}
                            value={participationTime}
                            unit="hr"
                        />
                        <StatCard
                            label={"PARTICIPATION\nNUMBER"}
                            value={participationNumber}
                            unit="time"
                        />
                    </div>

                    <WorkLogPanel
                        program="volunteer"
                        menuTo="/mypage/service-history?source=independent"
                        className="bg-[#ffeec6] min-h-[367px] xl:min-h-0 xl:flex-1"
                    />
                </div>

                {/* Right column — Upcoming takes the larger share of the two
                    because it carries the check-in and cancel actions. */}
                <div className="flex flex-col gap-[38px] min-h-0">
                    <Panel
                        title="Upcoming"
                        menuTo="/mypage/upcoming"
                        className="bg-[#d2d4d7] min-h-[296px] xl:min-h-0 xl:flex-[296_1_0%]"
                    >
                        <div className="grid grid-cols-[1fr_170px_100px] gap-x-4 pt-[16px] text-[16px] text-[#777f88] tracking-[0.8px]">
                            <span className="pl-[28px]">Event</span>
                            <span>Date</span>
                            <span />
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {upcomingEvents.length > 0 ? (
                                upcomingEvents.map((event) => (
                                    <div
                                        key={event.id}
                                        className="grid grid-cols-[1fr_170px_100px] gap-x-4 items-center h-[70px] text-[24px] text-[#5e5e5e]"
                                    >
                                        <span className="pl-[28px] truncate">{event.name}</span>
                                        <span>{event.date}</span>
                                        {/* One action at a time: Cancel until the check-in window
                                            opens, then Check in, then the ruling on the check-in.
                                            Attendance needs admin approval before it counts toward
                                            the stat cards, so pending, approved and no-show stay
                                            distinct. The branch hangs off the check-in timestamp
                                            rather than the status: a no-show ruling on an event that
                                            has not ended yet leaves Check in reachable, so someone
                                            marked absent early can still turn up. */}
                                        {event.attendedAt ? (
                                            <span
                                                className="text-[16px] text-[#777f88] tracking-[0.8px]"
                                                title={event.attendance.title}
                                            >
                                                {event.attendance.label}
                                            </span>
                                        ) : event.canCheckIn ? (
                                            <button
                                                type="button"
                                                onClick={() => checkInMutation.mutate(event.eventId)}
                                                disabled={checkInMutation.isPending}
                                                className="text-[16px] text-[#1c2938] tracking-[0.8px] text-left hover:underline disabled:opacity-50"
                                            >
                                                Check in
                                            </button>
                                        ) : (
                                            <button
                                                type="button"
                                                onClick={() => handleCancel(event.id)}
                                                disabled={cancelMutation.isPending}
                                                className="text-[16px] text-[#c52e2e] tracking-[0.8px] text-left hover:underline disabled:opacity-50"
                                            >
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <p className="pl-[28px] pt-[26px] text-[20px] text-[#777f88]">
                                    No upcoming events.
                                </p>
                            )}
                        </div>
                    </Panel>

                    <Panel
                        title="History"
                        menuTo="/mypage/service-history"
                        className="bg-[#e2dcd7] min-h-[254px] xl:min-h-0 xl:flex-[254_1_0%]"
                    >
                        <div className="grid grid-cols-[1fr_170px_100px] gap-x-4 pt-[16px] text-[16px] text-[#777f88] tracking-[0.8px]">
                            <span className="pl-[28px]">Activity</span>
                            <span>Date</span>
                            <span>Hours</span>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {activityRows.length > 0 ? (
                                activityRows.map((row) => (
                                    <div
                                        key={row.key}
                                        className="grid grid-cols-[1fr_170px_100px] gap-x-4 items-center h-[70px] text-[24px] text-[#5e5e5e]"
                                    >
                                        <span className="pl-[28px] truncate">{row.name}</span>
                                        <span>{row.date}</span>
                                        <span>{row.hours === null ? "—" : row.hours}</span>
                                    </div>
                                ))
                            ) : (
                                <p className="pl-[28px] pt-[26px] text-[20px] text-[#777f88]">
                                    No past activity.
                                </p>
                            )}
                        </div>
                    </Panel>
                </div>
            </div>
        </>
    );
}
